import {
    DestroyRef,
    effect,
    inject,
    Injectable,
    InjectionToken,
    signal,
    type Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { BuildingContext } from '@quipex/shared/contracts';
import { Subject } from 'rxjs';
import { debounceTime, exhaustMap, groupBy, mergeMap } from 'rxjs/operators';
import type { QuippyBuildingSession, QuippyScope } from '../models';
import { QuippyApi } from '../transport';
import {
    listConversations,
    readConversation,
    removeConversation,
} from './conversation-state';
import { dispatchRequest$ } from './dispatch-request';
import {
    hasPendingDocumentSelection,
    isDocumentSelectionPending,
} from './document-selection';
import {
    createEmptySession,
    appendNewThread,
    findThread,
    getQuippyIntroMessage,
    hasPendingAssistantMessage,
} from './helpers';
import { toQuippySessionKey, type MessageRequest } from './scope';
import { setDraftInSession } from './state';

const IDLE_MS = 60_000;

/** Internal authenticated identity hook; the public scope has no user/org key. */
export const QUIPPY_AUTH_IDENTITY = new InjectionToken<Signal<string | null>>(
    'QuippyAuthenticatedUserId'
);

@Injectable()
export class QuippySessionStore {
    private readonly destroyRef = inject(DestroyRef);
    private readonly api = inject(QuippyApi);
    private readonly authIdentity = inject(QUIPPY_AUTH_IDENTITY, {
        optional: true,
    });
    private generation = 0;
    private observedUserId: string | null = null;
    private readonly _sessions = signal<
        ReadonlyMap<string, QuippyBuildingSession>
    >(new Map());
    private readonly _messageRequests$ = new Subject<MessageRequest>();

    constructor() {
        this.setupConcurrentMessageRequestProcessing();
        if (this.authIdentity) {
            effect(() => {
                const userId = this.authIdentity?.() ?? null;
                if (this.observedUserId && userId !== this.observedUserId) {
                    this.clearAllConversations();
                }
                this.observedUserId = userId;
            });
        }
    }

    public getSession(scope: QuippyScope): QuippyBuildingSession {
        return this.readSession(scope);
    }

    public updateBuildingContext(
        scope: QuippyScope,
        context: BuildingContext | null,
        address?: string
    ): void {
        if (scope.kind !== 'building') return;
        const previous = this.readSession(scope);
        if (
            previous.buildingContext?.identifier === context?.identifier &&
            previous.buildingContext?.buildingName === context?.buildingName &&
            previous.buildingAddress === (context ? address : undefined)
        ) {
            return;
        }
        this.updateSession(scope, (session) => ({
            ...session,
            buildingContext: context ?? undefined,
            buildingAddress: context ? address : undefined,
        }));
    }

    public getBuildingDetails(scope: QuippyScope): {
        name: string;
        address?: string;
    } | null {
        if (scope.kind !== 'building') return null;
        const session = this.readSession(scope);
        const name = session.buildingContext?.buildingName;
        if (!name) return null;
        return {
            name,
            ...(session.buildingAddress
                ? { address: session.buildingAddress }
                : {}),
        };
    }

    /** A new presentation always receives an independent conversation ID. */
    public createConversation(scope: QuippyScope): string {
        let chatId = '';
        this.updateSession(scope, (session) => {
            const next = appendNewThread(session, getQuippyIntroMessage(scope));
            chatId = next.threads[0].id;
            return next;
        });
        return chatId;
    }

    public readConversation(chatId: string) {
        return readConversation(this._sessions(), chatId);
    }

    public listConversations() {
        return listConversations(this._sessions());
    }

    public deleteConversation(chatId: string): void {
        const conversation = this.readConversation(chatId);
        if (!conversation) return;
        this.updateSession(conversation.scope, (session) =>
            removeConversation(session, chatId)
        );
    }

    public clearAllConversations(): void {
        this.generation += 1;
        this._sessions.set(new Map());
    }

    public setDraft(scope: QuippyScope, chatId: string, draft: string): void {
        this.updateSession(scope, (session) =>
            setDraftInSession(session, chatId, draft)
        );
    }

    public sendMessage(
        scope: QuippyScope,
        chatId: string,
        userInput: string,
        adminMode = false
    ): void {
        const trimmed = userInput.trim();
        if (!trimmed) return;
        if (this.hasPendingAssistantMessage(scope, chatId)) return;
        if (hasPendingDocumentSelection(this.getSession(scope), chatId)) return;

        this._messageRequests$.next({
            type: 'send',
            scope,
            chatId,
            userInput: trimmed,
            adminMode,
        });
    }

    public retryMessage(
        scope: QuippyScope,
        chatId: string,
        messageId: string,
        adminMode = false
    ): void {
        if (!chatId || !messageId) return;
        if (this.hasPendingAssistantMessage(scope, chatId)) return;
        if (hasPendingDocumentSelection(this.getSession(scope), chatId)) return;

        this._messageRequests$.next({
            type: 'retry',
            scope,
            chatId,
            messageId,
            adminMode,
        });
    }

    public resendEditedMessage(
        scope: QuippyScope,
        chatId: string,
        messageId: string,
        userInput: string,
        adminMode = false
    ): void {
        const trimmed = userInput.trim();
        if (!chatId || !messageId || !trimmed) return;
        if (this.hasPendingAssistantMessage(scope, chatId)) return;
        if (hasPendingDocumentSelection(this.getSession(scope), chatId)) return;

        this._messageRequests$.next({
            type: 'edit-resend',
            scope,
            chatId,
            messageId,
            userInput: trimmed,
            adminMode,
        });
    }

    public selectDocumentOption(
        scope: QuippyScope,
        chatId: string,
        messageId: string,
        fileId: string
    ): void {
        if (!chatId || !messageId || !fileId) return;
        if (this.hasPendingAssistantMessage(scope, chatId)) return;
        if (hasPendingDocumentSelection(this.getSession(scope), chatId)) return;
        if (
            isDocumentSelectionPending(
                this.getSession(scope),
                chatId,
                messageId
            )
        ) {
            return;
        }

        this._messageRequests$.next({
            type: 'select-document',
            scope,
            chatId,
            messageId,
            fileId,
        });
    }

    public hasPendingAssistantMessage(
        scope: QuippyScope,
        chatId: string
    ): boolean {
        return hasPendingAssistantMessage(
            findThread(this.readSession(scope), chatId)
        );
    }

    private setupConcurrentMessageRequestProcessing(): void {
        this._messageRequests$
            .pipe(
                groupBy(
                    (request) =>
                        `${toQuippySessionKey(request.scope)}:${request.chatId}`,
                    {
                        duration: (group$) =>
                            group$.pipe(debounceTime(IDLE_MS)),
                    }
                ),
                mergeMap((group$) =>
                    group$.pipe(
                        exhaustMap((request) => this.dispatchRequest$(request))
                    )
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private dispatchRequest$(request: MessageRequest) {
        const generation = this.generation;
        return dispatchRequest$(request, {
            api: this.api,
            getSession: (scope) => this.getSession(scope),
            updateSession: (scope, updater) => {
                if (generation === this.generation) {
                    this.updateSession(scope, updater);
                }
            },
        });
    }

    private updateSession(
        scope: QuippyScope,
        updater: (session: QuippyBuildingSession) => QuippyBuildingSession
    ): void {
        const key = toQuippySessionKey(scope);
        this._sessions.update((previous) => {
            const next = new Map(previous);
            const current = next.get(key) ?? createEmptySession();
            next.set(key, updater(current));
            return next;
        });
    }

    private readSession(scope: QuippyScope): QuippyBuildingSession {
        const key = toQuippySessionKey(scope);
        return this._sessions().get(key) ?? createEmptySession();
    }
}
